
import React from "react";
import { Draggable } from "@hello-pangea/dnd";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Edit, Trash2, GripVertical } from "lucide-react";
import { ParcelType } from "@/hooks/useParcelTypes";
import { getIconByName } from "@/lib/icon-utils";
import { getIconGradientClasses } from "@/lib/color-utils";

interface ParcelTypeItemProps {
  parcelType: ParcelType;
  index: number;
  onEdit: (parcelType: ParcelType) => void;
  onDelete: (id: string) => void;
  onToggleEnabled: (parcelType: ParcelType) => void;
  reordering: boolean;
}

const ParcelTypeItem = ({ 
  parcelType, 
  index, 
  onEdit, 
  onDelete, 
  onToggleEnabled, 
  reordering 
}: ParcelTypeItemProps) => {
  const IconComponent = getIconByName(parcelType.icon_name);
  const iconGradientClasses = getIconGradientClasses(parcelType.color_from, parcelType.color_to);

  return (
    <Draggable key={parcelType.id} draggableId={parcelType.id} index={index} isDragDisabled={reordering}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`flex items-center justify-between p-4 border rounded-lg bg-card transition-all ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''
          } ${reordering ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center gap-4">
            <div {...provided.dragHandleProps} className={reordering ? 'cursor-not-allowed' : 'cursor-grab'}>
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className={`p-2 rounded-lg bg-gradient-to-br ${iconGradientClasses} text-white`}>
              <IconComponent className="h-5 w-5" />
            </div>
            <div>
              <div className="font-medium">{parcelType.title}</div>
              <div className="text-sm text-muted-foreground">{parcelType.description}</div>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline">{parcelType.key}</Badge>
                {!parcelType.is_enabled && <Badge variant="warning">Disabled</Badge>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={parcelType.is_enabled}
              onCheckedChange={() => onToggleEnabled(parcelType)}
              disabled={reordering}
            />
            <Button variant="outline" size="sm" onClick={() => onEdit(parcelType)} disabled={reordering}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onDelete(parcelType.id)} disabled={reordering}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default ParcelTypeItem;
